<template>
  <v-dialog v-model="value" max-width="800px" persistent>
    <v-card :loading="loading" :disabled="loading">
      <v-card-title class="justify-center">
        <span class="text-h5 sp">{{ $t('students.create') }}</span>
      </v-card-title>

      <v-card-text>
        <v-container v-if="!created">
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-row>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="form.baseCountryId"
                  :items="countries"
                  item-text="name"
                  item-value="id"
                  :label="$t('students.country')"
                  :rules="[rules.required]"
                  :disabled="lockContext"
                  @change="onCountryChange"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="form.schoolId"
                  :items="schools"
                  item-text="text"
                  item-value="value"
                  :label="$t('students.school')"
                  :rules="[rules.required]"
                  :disabled="lockContext || !form.baseCountryId"
                  @change="onSchoolChange"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="form.courseId"
                  :items="courses"
                  item-text="name"
                  item-value="id"
                  :label="$t('students.course')"
                  :rules="[rules.required]"
                  :disabled="!form.schoolId"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.firstname"
                  :label="$t('students.firstname')"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.lastname"
                  :label="$t('students.lastname')"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.lastname2nd"
                  :label="$t('students.lastname2nd')"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="4">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.birthDate"
                      :label="$t('students.birth_date')"
                      prepend-icon="mdi-calendar"
                      readonly
                      :rules="[rules.required]"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker v-model="form.birthDate" @input="dateMenu = false" />
                </v-menu>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.dni"
                  :label="$t('students.dni')"
                  :rules="[rules.required, rules.dni]"
                  :disabled="form.generateUsername"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.email"
                  :label="$t('students.email')"
                  :rules="[rules.emailOptional]"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-checkbox
                  v-model="form.generateUsername"
                  :label="$t('students.generate_username')"
                  @change="onToggleGenerateUsername"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-container>

        <v-container v-else class="text-center">
          <v-icon size="48" color="green">mdi-check-circle</v-icon>
          <h3 class="mt-2">{{ $t('students.created_title') }}</h3>
          <p class="mt-2">
            {{ createdCredentials }}
          </p>
          <p class="grey--text">{{ $t('students.created_must_change') }}</p>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <template v-if="!created">
          <v-btn color="grey" text @click="close">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="deep-purple" text :disabled="!valid" @click="save">
            {{ $t('common.save') }}
          </v-btn>
        </template>
        <template v-else>
          <v-btn color="deep-purple" text @click="resetForAnother">
            {{ $t('students.create_another') }}
          </v-btn>
          <v-btn color="grey" text @click="close">{{ $t('common.close') }}</v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import countryService from '../../../services/country.service';
import schoolService from '../../../services/school.service';
import courseService from '../../../services/course.service';
import studentService from '../../../services/student.service';
import snackbarMixin from '../../../mixins/snackbar';
import { COUNTRIES_CUSTOMER_ID } from '../../../constants/data';

const emptyForm = () => ({
  baseCountryId: null,
  schoolId: null,
  courseId: null,
  firstname: '',
  lastname: '',
  lastname2nd: '',
  birthDate: '2007-05-01',
  dni: '',
  email: '',
  generateUsername: false,
});

export default {
  name: 'CreateStudentModal',
  mixins: [snackbarMixin],
  props: {
    value: { type: Boolean, default: false },
    schoolId: { type: [String, Number], default: null },
    countryId: { type: [String, Number], default: null },
    isStudent: { type: Boolean, default: false },
  },
  data: () => ({
    loading: false,
    valid: false,
    dateMenu: false,
    countries: [],
    schools: [],
    courses: [],
    form: emptyForm(),
    created: false,
    createdInfo: { username: '', passwordTmp: '' },
    rules: {
      required: (v) => (v !== null && v !== undefined && v !== '') || 'Requerido.',
      dni: (v) => /^\S{8,}$/.test(v || '') || 'Revisa el formato o espacios en blanco del DNI',
      emailOptional: (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Correo inválido.',
    },
  }),
  computed: {
    lockContext() {
      return !!(this.schoolId && this.countryId);
    },
    createdCredentials() {
      return this.$t('students.created_credentials', {
        username: this.createdInfo.username,
        password: this.createdInfo.passwordTmp,
      });
    },
  },
  async mounted() {
    await this.loadCountries();
    if (this.countryId) {
      this.form.baseCountryId = Number(this.countryId);
      await this.loadSchools(this.form.baseCountryId);
    }
    if (this.schoolId) {
      const match = this.schools.find((s) => String(s.value) === String(this.schoolId));
      this.form.schoolId = match ? match.value : this.schoolId;
      await this.loadCourses(this.schoolId);
    }
  },
  methods: {
    async loadCountries() {
      try {
        const { data } = await countryService.findAll();
        this.countries = (data || []).filter((c) => COUNTRIES_CUSTOMER_ID.includes(c.id));
      } catch (e) {
        this.countries = [];
      }
    },
    async loadSchools(countryId) {
      try {
        const { status, data } = await schoolService.getListByCountry(countryId);
        this.schools = status === 200 ? [...data] : [];
      } catch (e) {
        this.schools = [];
        this.snackbarError(e.message);
      }
    },
    async loadCourses(schoolId) {
      try {
        const { status, data } = await courseService.findAllBySchool(schoolId);
        this.courses = status === 200 ? [...data] : [];
      } catch (e) {
        this.courses = [];
        this.snackbarError(e.message);
      }
    },
    async onCountryChange() {
      this.form.schoolId = null;
      this.form.courseId = null;
      this.schools = [];
      this.courses = [];
      if (this.form.baseCountryId) await this.loadSchools(this.form.baseCountryId);
    },
    async onSchoolChange() {
      this.form.courseId = null;
      this.courses = [];
      if (this.form.schoolId) await this.loadCourses(this.form.schoolId);
    },
    onToggleGenerateUsername(checked) {
      if (!checked) return;
      if (!this.form.birthDate) {
        this.form.generateUsername = false;
        this.snackbarError('Elige la fecha de nacimiento primero.');
        return;
      }
      const [, month, day] = this.form.birthDate.split('-');
      const u = (
        this.form.firstname.slice(0, 3)
        + this.form.lastname.slice(0, 2)
        + (this.form.lastname2nd || '').slice(0, 1)
        + day
        + month
      ).toLowerCase();
      this.form.dni = u;
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      try {
        const result = await studentService.create({
          firstname: this.form.firstname,
          lastname: this.form.lastname,
          lastname2nd: this.form.lastname2nd || undefined,
          dni: this.form.dni,
          email: this.form.email || undefined,
          birthDate: this.form.birthDate,
          baseCountryId: this.form.baseCountryId,
          schoolId: this.form.schoolId,
          courseId: this.form.courseId,
        });
        this.createdInfo = {
          username: result.content.username,
          passwordTmp: result.content.passwordTmp,
        };
        this.created = true;
        this.$emit('onSave');
      } catch (error) {
        const status = error.response && error.response.status;
        if (status === 409) {
          this.snackbarError(this.$t('students.dni_exists'));
        } else {
          const msg = error.response && error.response.data && error.response.data.message;
          this.snackbarError(msg || this.$t('students.create_error'));
        }
      } finally {
        this.loading = false;
      }
    },
    resetForAnother() {
      const { baseCountryId, schoolId } = this.form;
      this.form = { ...emptyForm(), baseCountryId, schoolId };
      this.created = false;
      this.createdInfo = { username: '', passwordTmp: '' };
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    close() {
      this.form = emptyForm();
      this.created = false;
      if (this.$refs.form) this.$refs.form.resetValidation();
      this.$emit('onCancel');
    },
  },
};
</script>

<style scoped>
span.sp {
  font-family: "Space Grotesk" !important;
}
</style>
